/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useRef, useState, ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { useSideNavContext } from './context/useSideNavContext'
import { clearAllLastSelectedFolderIds } from '../../utils/lastSelectedFolderStorage'

interface SideNavItemCustomProps {
  className?: string
  classNameLink?: string
  title?: string
  to: string
  children: ReactNode
  id?: string
  onClick?: () => void
  name?: string
  style?: React.CSSProperties
}

export const SideNavItemCustom: React.FC<SideNavItemCustomProps> = ({
  className = '',
  classNameLink = '',
  title,
  to,
  children,
  id,
  onClick,
  name,
  style
}) => {
  const sideNavContext = useSideNavContext()
  const location = useLocation()
  const [isActive, setIsActive] = useState(false)
  const thisRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    evaluateState()
  }, [location.pathname, to])

  useEffect(() => {
    evaluateState()
  }, [])

  function evaluateState() {
    // Check if current path matches the target path
    const currentlyActive = location.pathname === to || location.pathname.startsWith(to + '/')

    if (currentlyActive && !isActive) {
      activate()
    }
    if (!currentlyActive) {
      setIsActive(false)
    }
  }

  function activate() {
    sideNavContext.setActiveMenuItem(thisRef)
    sideNavContext.setActiveMenuSection(thisRef)
    sideNavContext.setExpandedMenuSection(thisRef)
    setIsActive(true)
  }

  function handleClick() {
    sideNavContext.setExpandedMenuSection(thisRef)

    // Keep the stored picker location only for the explicit All Folders/Documents entry.
    // For every other nav item, clear it so picker preload does not restore stale site/study paths.
    const shouldPreserveSelection = name === 'ALL_FOLDERS_DOCUMENTS' || to.startsWith('/browser')
    if (!shouldPreserveSelection) {
      try {
        clearAllLastSelectedFolderIds()
      } catch {
        // Ignore storage errors.
      }
    }

    if (onClick) {
      onClick()
    }
  }

  return (
    <li style={style} className={`side-nav-item${isActive ? ' active' : ''}${className ? ' ' + className : ''}`}>
      <Link ref={thisRef} id={id} to={to} onClick={handleClick} className={classNameLink} title={title}>
        {children}
      </Link>
    </li>
  )
}
